import React from "react";
import {
  Box, Checkbox, IconButton, LinearProgress, Paper, Typography, alpha, Chip,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import FlightTakeoffIcon from "@mui/icons-material/FlightTakeoff";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import {
  useGetPilotChecklistQuery, useSetPilotChecklistItemMutation,
} from "../../../Api/attoDeskApi";
import { useAppSelector } from "../../../Store/hooks";
import { selectIsSuperAdmin } from "../../../Store/Auth/AuthSelector";
import PageLoader from "../../../Components/Loader/PageLoader";
import { useNotifier } from "../../../Core/Notifier";
import {
  NAVY, GOLD_SOFT, BORDER, TEXT_PRIMARY, TEXT_SECONDARY,
} from "../../../theme/tokens";

interface IItem {
  id: string;
  label: string;
  category: string;
  checked: boolean;
}

const CATEGORY_COLOR: Record<string, string> = {
  auth:     "#3b82f6",
  tenant:   "#7c3aed",
  content:  "#0e7c9b",
  player:   "#10b981",
  commands: "#f59e0b",
  billing:  "#d4af37",
  ops:      "#64748b",
};

const PilotChecklistPage: React.FC = () => {
  const isSuper = useAppSelector(selectIsSuperAdmin);
  const { showMessage, showErrorMessage } = useNotifier();

  const { data, isLoading, refetch, isFetching } = useGetPilotChecklistQuery(undefined, {
    skip: !isSuper,
  });
  const [setItem, { isLoading: isSaving }] = useSetPilotChecklistItemMutation();

  if (!isSuper) {
    return (
      <Box sx={{ p: 6, textAlign: "center" }}>
        <Typography sx={{ fontSize: 14, color: TEXT_SECONDARY }}>
          Pilot checklist is visible to Super Admins only.
        </Typography>
      </Box>
    );
  }
  if (isLoading) return <PageLoader height="20vh" />;

  const items: IItem[] = Array.isArray(data?.data) ? (data?.data as unknown as IItem[]) : [];
  const meta = (data as any)?.meta || { total: 0, checked: 0 };
  const pct = meta.total > 0 ? Math.round((meta.checked / meta.total) * 100) : 0;

  // Group by category for visual grouping
  const grouped: Record<string, IItem[]> = {};
  items.forEach((i) => {
    grouped[i.category] = grouped[i.category] || [];
    grouped[i.category].push(i);
  });

  const toggle = async (item: IItem) => {
    try {
      const res = await setItem({ id: item.id, checked: !item.checked }).unwrap();
      if (res.status) showMessage(item.checked ? "Unchecked" : "Marked complete");
    } catch (e: any) { showErrorMessage(e?.data?.message || "Failed"); }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
        <Box sx={{
          width: 40, height: 40, borderRadius: 1.5,
          display: "flex", alignItems: "center", justifyContent: "center",
          background: `linear-gradient(135deg, ${alpha("#10b981", 0.18)} 0%, ${alpha("#10b981", 0.06)} 100%)`,
          color: "#10b981",
        }}>
          <FlightTakeoffIcon />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography fontSize={20} fontWeight={800} color={NAVY} letterSpacing="-0.02em">
            Pilot QA Checklist
          </Typography>
          <Typography fontSize={12} color={TEXT_SECONDARY}>
            Walk through this list with the pilot customer on-site. Progress is saved server-side.
            {isFetching ? " · syncing…" : ""}
          </Typography>
        </Box>
        <IconButton onClick={() => refetch()}
          sx={{ border: `1px solid ${BORDER}`, borderRadius: 2, width: 38, height: 38, bgcolor: "#fff" }}>
          <RefreshIcon sx={{ fontSize: 18, color: NAVY }} />
        </IconButton>
      </Box>

      {/* Progress */}
      <Paper elevation={0} sx={{
        p: 3, mb: 3, borderRadius: 3, border: `1px solid ${BORDER}`, bgcolor: "#fff",
      }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 700, color: TEXT_SECONDARY,
            textTransform: "uppercase", letterSpacing: "0.06em" }}>
            Pilot Readiness
          </Typography>
          <Typography sx={{ fontSize: 12, fontWeight: 700, color: pct === 100 ? "#10b981" : NAVY }}>
            {meta.checked} / {meta.total} complete · {pct}%
          </Typography>
        </Box>
        <LinearProgress variant="determinate" value={pct}
          sx={{
            height: 8, borderRadius: 4, bgcolor: "#f1f5f9",
            "& .MuiLinearProgress-bar": {
              bgcolor: pct === 100 ? "#10b981" : GOLD_SOFT,
              borderRadius: 4,
            },
          }} />
        {pct === 100 && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 2 }}>
            <CheckCircleIcon sx={{ color: "#10b981", fontSize: 20 }} />
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#10b981" }}>
              All items checked — ready to hand over.
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Items grouped */}
      {Object.entries(grouped).map(([category, list]) => (
        <Paper key={category} elevation={0} sx={{
          mb: 2, borderRadius: 3, border: `1px solid ${BORDER}`, bgcolor: "#fff", overflow: "hidden",
        }}>
          <Box sx={{
            px: 3, py: 1.5,
            background: `linear-gradient(135deg, ${alpha(CATEGORY_COLOR[category] || "#475569", 0.06)} 0%, transparent 100%)`,
            borderBottom: `1px solid ${BORDER}`,
          }}>
            <Typography sx={{ fontSize: 11, fontWeight: 700,
              color: CATEGORY_COLOR[category] || "#475569",
              textTransform: "uppercase", letterSpacing: "0.08em" }}>
              {category.replace(/_/g, " ")}
            </Typography>
          </Box>
          {list.map((item) => (
            <Box key={item.id} sx={{
              display: "flex", alignItems: "center", gap: 1.5, p: 2,
              borderBottom: `1px solid ${BORDER}`,
              "&:last-child": { borderBottom: "none" },
              bgcolor: item.checked ? alpha("#dcfce7", 0.3) : "transparent",
              cursor: isSaving ? "wait" : "pointer",
              "&:hover": { bgcolor: alpha("#dcfce7", 0.5) },
            }}
              onClick={() => !isSaving && toggle(item)}>
              <Checkbox checked={item.checked} disabled={isSaving}
                onClick={(e) => e.stopPropagation()}
                onChange={() => toggle(item)}
                sx={{ color: TEXT_SECONDARY, "&.Mui-checked": { color: "#10b981" } }} />
              <Typography sx={{
                fontSize: 14, fontWeight: 600,
                color: item.checked ? TEXT_SECONDARY : TEXT_PRIMARY,
                textDecoration: item.checked ? "line-through" : "none",
                flex: 1,
              }}>
                {item.label}
              </Typography>
              <Chip size="small" label={item.id}
                sx={{ height: 18, fontSize: 9.5, fontWeight: 700,
                  bgcolor: "#f1f5f9", color: TEXT_SECONDARY, fontFamily: "monospace" }} />
            </Box>
          ))}
        </Paper>
      ))}
    </Box>
  );
};

export default PilotChecklistPage;
